import {
  FONT_STACKS,
  boardToScreen,
  connectorGeometry,
  fitTextToBox,
  layoutText,
  measuredHeight,
  rectCenter,
  shapeTextRect,
  vRotate,
  type BoardNode,
  type NodeLookup,
  type Rect,
  type TextStyle,
  type Vec,
} from '@kanvas/board-core';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { BoardDoc } from '../collab/BoardDoc';
import { STICKY_PADDING, TEXT_PADDING } from '../render/drawNode';
import { measureText } from '../render/measure';
import { useEditor } from '../state/store';
import { Icon } from '../ui/icons';

export interface TextEditorOverlayProps {
  doc: BoardDoc;
}

/**
 * Inline text editing.
 *
 * A real `<textarea>` is positioned over the node and styled to match what the
 * canvas draws. Rendering the caret and selection ourselves would mean
 * reimplementing IME composition, spellcheck, accessibility and every
 * platform's text-navigation shortcuts; letting the browser own the text and
 * mirroring it into the document is both less code and more correct.
 *
 * The canvas hides the node's own text while it is being edited, so the two
 * never double up.
 */
export function TextEditorOverlay({ doc }: TextEditorOverlayProps) {
  const editingId = useEditor((s) => s.editingId);
  const viewport = useEditor((s) => s.viewport);
  const ref = useRef<HTMLTextAreaElement>(null);

  const node = editingId ? doc.get(editingId) : undefined;

  /*
   * What the textarea shows, updated in the same tick as the keystroke. The
   * document notifies React a frame later, so binding the textarea straight
   * to it handed the old text back after each key and the browser moved the
   * caret to the end — editing anywhere but the end lost your place after
   * one character. A remote edit (or another node) replaces the draft.
   */
  const docText = node && 'text' in node ? (node as { text: string }).text : '';
  const [draft, setDraft] = useState({ id: editingId, text: docText });
  useEffect(() => {
    setDraft((d) => (d.id === editingId && d.text === docText ? d : { id: editingId, text: docText }));
  }, [editingId, docText]);
  const liveText = draft.id === editingId ? draft.text : docText;
  const writeText = (next: string) => {
    if (!node) return;
    setDraft({ id: node.id, text: next });
    doc.update(node.id, { text: next });
  };

  // Focus when editing starts. The caret goes where the text was clicked
  // (double-click, or a click with the text tool), so you can carry on
  // writing at any spot; opened any other way (Enter, a new node) it goes
  // to the end.
  useEffect(() => {
    if (!editingId) return;
    const el = ref.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    let index = el.value.length;
    const state = useEditor.getState();
    const current = doc.get(editingId);
    if (state.editingCaret && current) {
      // The layer is rotated about the node's centre; undo that first.
      const centre = rectCenter({ x: current.x, y: current.y, w: current.w, h: current.h });
      const board =
        Number.isFinite(current.rotation) && current.rotation !== 0
          ? vRotate(state.editingCaret, centre, -current.rotation)
          : state.editingCaret;
      const screen = boardToScreen(state.viewport, board);
      const local = {
        x: screen.x - (parseFloat(el.style.left) || 0),
        y: screen.y - (parseFloat(el.style.top) || 0),
      };
      index = textIndexAtPoint(el, local) ?? index;
    }
    el.setSelectionRange(index, index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  // Sticky notes and auto-height text grow as the user types.
  //
  // Height is stored as an integer (`Math.ceil`). Comparing the *float* layout
  // height to that integer with a 0.5px epsilon never settles when the
  // fractional part is in (0, 0.5) — e.g. 16px × 1.32 line-height = 21.12,
  // ceil 22, |21.12 − 22| = 0.88, write 22 again, `updatedAt` changes, React
  // re-renders the board, and the tab paints white. Always compare against the
  // integer we would store.
  useLayoutEffect(() => {
    if (!node) return;
    if (node.type === 'sticky' && node.autoGrow) {
      const needed =
        measuredHeight(node.text, node.textStyle, node.w - STICKY_PADDING * 2, measureText) +
        STICKY_PADDING * 2;
      const nextH = Math.ceil(needed);
      if (Number.isFinite(nextH) && nextH > node.h) doc.update(node.id, { h: nextH });
    }
    if (node.type === 'text' && node.autoHeight) {
      const layout = layoutText(node.text, node.textStyle, node.w - TEXT_PADDING * 2, measureText);
      const min = node.textStyle.fontSize * node.textStyle.lineHeight;
      const nextH = Math.ceil(Math.max(layout.height, min));
      if (Number.isFinite(nextH) && nextH !== node.h) doc.update(node.id, { h: nextH });
    }
  }, [doc, node]);

  const geometry = useMemo(() => {
    if (!node) return null;
    const box = textBoxFor(node, doc.lookup);
    if (!box) return null;
    const topLeft = boardToScreen(viewport, { x: box.x, y: box.y });
    const center = boardToScreen(viewport, rectCenter({ x: node.x, y: node.y, w: node.w, h: node.h }));
    return { box, topLeft, center };
  }, [doc, node, viewport]);

  if (!node || !geometry || !('text' in node)) return null;

  const declaredStyle = (node as { textStyle: TextStyle }).textStyle;
  const { box, topLeft, center } = geometry;
  const zoom = viewport.zoom;
  const text = liveText;

  // Shapes (and stickies with auto-fit) shrink their text to fit. The editor
  // has to use the same computed size, or the caret would sit at a different
  // scale than the glyphs the canvas draws around it.
  const autoFits = node.type === 'shape' || (node.type === 'sticky' && !node.autoGrow);
  const style: TextStyle = autoFits
    ? { ...declaredStyle, fontSize: fitTextToBox(text || ' ', declaredStyle, box, measureText).fontSize }
    : declaredStyle;

  const verticalOffset = (() => {
    // Match the canvas vertical alignment by translating the textarea rather
    // than relying on flexbox, which cannot centre a growing textarea reliably.
    const layout = layoutText(text || ' ', style, box.w, measureText);
    const contentHeight = layout.height;
    if (style.valign === 'top') return 0;
    if (style.valign === 'bottom') return Math.max(box.h - contentHeight, 0);
    return Math.max((box.h - contentHeight) / 2, 0);
  })();

  return (
    <div
      className="text-editor-layer"
      style={{
        transform:
          Number.isFinite(node.rotation) && node.rotation !== 0
            ? `rotate(${node.rotation}rad)`
            : undefined,
        transformOrigin: `${center.x}px ${center.y}px`,
      }}
    >
      <RichTextToolbar
        textareaRef={ref}
        text={text}
        top={topLeft.y - 34}
        left={topLeft.x}
        onChange={writeText}
      />
      <textarea
        ref={ref}
        className="text-editor"
        value={text}
        spellCheck={false}
        onChange={(e) => writeText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            useEditor.getState().setEditing(null);
            return;
          }
          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
            e.preventDefault();
            toggleWrap(e.currentTarget, text, '**', writeText);
            return;
          }
          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'i') {
            e.preventDefault();
            toggleWrap(e.currentTarget, text, '*', writeText);
            return;
          }
          // Enter commits on single-line-ish nodes; Shift+Enter always breaks.
          if (e.key === 'Enter' && !e.shiftKey && node.type === 'connector') {
            e.preventDefault();
            useEditor.getState().setEditing(null);
            return;
          }
          if (e.key === 'Tab') {
            e.preventDefault();
            useEditor.getState().setEditing(null);
          }
          // Keep board shortcuts from firing while typing.
          e.stopPropagation();
        }}
        onBlur={() => useEditor.getState().setEditing(null)}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          left: `${topLeft.x}px`,
          top: `${topLeft.y + verticalOffset * zoom}px`,
          width: `${clampCssPx(box.w * zoom)}px`,
          height: `${clampCssPx(Math.max(box.h - verticalOffset, style.fontSize * style.lineHeight) * zoom)}px`,
          fontSize: `${style.fontSize * zoom}px`,
          lineHeight: `${style.lineHeight}`,
          fontFamily: FONT_STACKS[style.fontFamily] ?? FONT_STACKS.sans,
          fontWeight: style.bold ? 600 : 400,
          fontStyle: style.italic ? 'italic' : 'normal',
          textDecoration: [
            style.underline ? 'underline' : '',
            style.strike ? 'line-through' : '',
          ]
            .filter(Boolean)
            .join(' ') || 'none',
          textAlign: style.align,
          color: style.color,
          caretColor: style.color,
        }}
      />
    </div>
  );
}

/**
 * Character index nearest to `point` (textarea-local CSS px).
 *
 * A textarea can't hit-test its own text, so an invisible div copies its
 * font, width and wrapping, and a marker span reports where each index sits.
 * Positions only ever move right along a line and down between lines, so a
 * binary search needs about log2(length) layouts, not one per character.
 */
function textIndexAtPoint(el: HTMLTextAreaElement, point: Vec): number | null {
  const parent = el.parentElement;
  if (!parent) return null;
  const cs = getComputedStyle(el);
  const mirror = document.createElement('div');
  for (const prop of [
    'font',
    'letterSpacing',
    'lineHeight',
    'textAlign',
    'textTransform',
    'textIndent',
    'wordSpacing',
    'whiteSpace',
    'overflowWrap',
    'wordBreak',
    'tabSize',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'boxSizing',
    'width',
  ] as const) {
    mirror.style[prop] = cs[prop];
  }
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.pointerEvents = 'none';
  mirror.style.left = '0';
  mirror.style.top = '0';
  mirror.style.height = 'auto';
  parent.appendChild(mirror);

  const text = el.value;
  const lineH = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.3 || 16;
  // The whole text is laid out, with the marker inside it: text after the
  // marker decides how a centred line is placed and where a word wraps.
  const pos = (i: number): Vec => {
    mirror.textContent = text.slice(0, i);
    const marker = document.createElement('span');
    marker.textContent = '\u2060'; // word joiner: no break opportunity
    mirror.appendChild(marker);
    mirror.appendChild(document.createTextNode(text.slice(i)));
    return { x: marker.offsetLeft, y: marker.offsetTop };
  };
  // Compare by line number, not raw y: a marker sits a little below its
  // line's top, and a point left of a centred line still belongs to it.
  const padTop = parseFloat(cs.paddingTop) || 0;
  const rowOf = (y: number) => Math.floor((y - padTop) / lineH);
  const targetRow = Math.max(0, rowOf(point.y));
  const before = (p: Vec) => {
    const row = rowOf(p.y);
    return row < targetRow || (row === targetRow && p.x <= point.x);
  };

  try {
    let lo = 0;
    let hi = text.length;
    // Largest index still before the point (or 0).
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (before(pos(mid))) lo = mid;
      else hi = mid - 1;
    }
    if (lo >= text.length) return lo;
    const a = pos(lo);
    const b = pos(lo + 1);
    const aRow = rowOf(a.y);
    const bRow = rowOf(b.y);
    // Point is left of where its line starts: caret at the line's start.
    // After a line break, or a space left hanging at the end of a wrapped
    // line, that is the index past it. Where a long word is broken mid-word,
    // the same index is both the end of one line and the start of the next,
    // and browsers show it at the start of the next.
    if (aRow < targetRow && bRow === targetRow) return /\s/.test(text[lo] ?? '') ? lo + 1 : lo;
    // Otherwise the nearer side of the character under the point.
    if (aRow === bRow && Math.abs(b.x - point.x) < Math.abs(a.x - point.x)) return lo + 1;
    return lo;
  } finally {
    mirror.remove();
  }
}

/**
 * Wraps or unwraps the current textarea selection with markup, keeping the
 * same text selected afterwards so a second click toggles it back off.
 */
function toggleWrap(
  el: HTMLTextAreaElement,
  text: string,
  marker: string,
  onChange: (next: string) => void,
): void {
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const before = text.slice(Math.max(0, start - marker.length), start);
  const after = text.slice(end, end + marker.length);
  const selected = text.slice(start, end);

  if (before === marker && after === marker) {
    const next = text.slice(0, start - marker.length) + selected + text.slice(end + marker.length);
    onChange(next);
    requestAnimationFrame(() => el.setSelectionRange(start - marker.length, end - marker.length));
    return;
  }

  const next = text.slice(0, start) + marker + selected + marker + text.slice(end);
  onChange(next);
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(start + marker.length, end + marker.length);
  });
}

function toggleBulletLines(el: HTMLTextAreaElement, text: string, onChange: (next: string) => void): void {
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  const nextBreak = text.indexOf('\n', end - 1);
  const lineEnd = nextBreak === -1 ? text.length : nextBreak;

  const lines = text.slice(lineStart, lineEnd).split('\n');
  const allBulleted = lines.every((l) => l === '' || l.startsWith('- '));
  const nextLines = lines.map((l) => {
    if (l === '') return l;
    return allBulleted ? l.slice(2) : `- ${l}`;
  });
  const block = nextLines.join('\n');
  const next = text.slice(0, lineStart) + block + text.slice(lineEnd);
  onChange(next);
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(lineStart, lineStart + block.length);
  });
}

function insertLink(el: HTMLTextAreaElement, text: string, onChange: (next: string) => void): void {
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const label = text.slice(start, end) || 'ссылка';
  const url = window.prompt('Адрес ссылки', 'https://');
  if (!url) return;
  const markup = `[${label}](${url})`;
  const next = text.slice(0, start) + markup + text.slice(end);
  onChange(next);
  requestAnimationFrame(() => el.focus());
}

/**
 * Formatting buttons for the inline editor.
 *
 * They write the same lightweight markup the canvas renders (`**bold**`,
 * `- ` bullets, `[label](url)` links) straight into the textarea's own
 * selection, so formatting a note needs no separate rich-text data model —
 * `mousedown` is prevented so the textarea never loses focus or its
 * selection before the click is handled.
 */
function RichTextToolbar({
  textareaRef,
  text,
  top,
  left,
  onChange,
}: {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  text: string;
  top: number;
  left: number;
  onChange: (next: string) => void;
}) {
  const run = (fn: (el: HTMLTextAreaElement) => void) => {
    const el = textareaRef.current;
    if (el) fn(el);
  };

  return (
    <div
      className="rich-toolbar"
      style={{ top: `${top}px`, left: `${left}px` }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button type="button" title="Жирный (Ctrl B)" onMouseDown={(e) => e.preventDefault()} onClick={() => run((el) => toggleWrap(el, text, '**', onChange))}>
        <Icon name="bold" size={13} />
      </button>
      <button type="button" title="Курсив (Ctrl I)" onMouseDown={(e) => e.preventDefault()} onClick={() => run((el) => toggleWrap(el, text, '*', onChange))}>
        <Icon name="italic" size={13} />
      </button>
      <button type="button" title="Зачёркнутый" onMouseDown={(e) => e.preventDefault()} onClick={() => run((el) => toggleWrap(el, text, '~~', onChange))}>
        <Icon name="strike" size={13} />
      </button>
      <button type="button" title="Список" onMouseDown={(e) => e.preventDefault()} onClick={() => run((el) => toggleBulletLines(el, text, onChange))}>
        <Icon name="list" size={13} />
      </button>
      <button type="button" title="Ссылка" onMouseDown={(e) => e.preventDefault()} onClick={() => run((el) => insertLink(el, text, onChange))}>
        <Icon name="link" size={13} />
      </button>
    </div>
  );
}

/**
 * CSS lengths on the overlay. NaN/Infinity from a bad node, or a box the size
 * of the whole board, would otherwise create a layer that covers the page.
 */
function clampCssPx(value: number): number {
  if (!Number.isFinite(value)) return 8;
  return Math.min(Math.max(value, 8), 16_000);
}

/** The rectangle the canvas draws text into, mirrored by the editor. */
function textBoxFor(node: BoardNode, lookup: NodeLookup): Rect | null {
  switch (node.type) {
    case 'sticky':
      return {
        x: node.x + STICKY_PADDING,
        y: node.y + STICKY_PADDING,
        w: Math.max(node.w - STICKY_PADDING * 2, 1),
        h: Math.max(node.h - STICKY_PADDING * 2, 1),
      };
    case 'text':
      return {
        x: node.x + TEXT_PADDING,
        y: node.y,
        w: Math.max(node.w - TEXT_PADDING * 2, 1),
        h: Math.max(node.h, 1),
      };
    case 'shape':
      return shapeTextRect(node.shape, { x: node.x, y: node.y, w: node.w, h: node.h });
    case 'connector': {
      // The label sits at the midpoint of the resolved route, so the editor has
      // to ask the same geometry the renderer uses.
      const mid = connectorGeometry(node, lookup).mid;
      const h = node.textStyle.fontSize * node.textStyle.lineHeight;
      return { x: mid.x - 90, y: mid.y - h / 2, w: 180, h };
    }
    default:
      return null;
  }
}
